import { z } from "zod";
import { parsePhoneNumberFromString } from "libphonenumber-js";

/**
 * Phone number field - optional, validates E.164 format.
 * Accepts:
 * - Empty string (optional field)
 * - Full E.164 format (e.g., "+15551234567")
 *
 * The PhoneInput component stores values in E.164 format.
 */
export const phoneNumberField = z
  .string()
  .optional()
  .refine(
    (val) => {
      if (!val || val === "") return true;

      // Parse and validate with libphonenumber-js
      const parsed = parsePhoneNumberFromString(val);
      return parsed?.isValid() ?? false;
    },
    {
      message: "Please enter a valid phone number",
    }
  );

/**
 * Required phone number field - validates E.164 format.
 */
export const phoneNumberFieldRequired = z
  .string()
  .min(1, "Phone number is required")
  .refine(
    (val) => {
      const parsed = parsePhoneNumberFromString(val);
      return parsed?.isValid() ?? false;
    },
    {
      message: "Please enter a valid phone number",
    }
  );

/**
 * Confirm phone number field - for signup validation.
 * Used with .refine() at the schema level to compare with phoneNumber.
 */
export const confirmPhoneNumberField = z.string().optional();

/**
 * Helper to create a phone match refinement for schemas.
 * Use in the form schema's .refine() or .superRefine() call.
 *
 * @example
 * const schema = z.object({
 *   phoneNumber: phoneNumberField,
 *   confirmPhoneNumber: confirmPhoneNumberField,
 * }).refine(
 *   (data) => phoneNumbersMatch(data.phoneNumber, data.confirmPhoneNumber),
 *   { message: "Phone numbers must match", path: ["confirmPhoneNumber"] }
 * );
 */
export function phoneNumbersMatch(
  phoneNumber: string | undefined,
  confirmPhoneNumber: string | undefined
): boolean {
  // If both are empty, they match
  if (!phoneNumber && !confirmPhoneNumber) return true;

  // If one is empty and the other is not, they don't match
  if (!phoneNumber || !confirmPhoneNumber) return false;

  // Compare normalized E.164 values
  const parsed1 = parsePhoneNumberFromString(phoneNumber);
  const parsed2 = parsePhoneNumberFromString(confirmPhoneNumber);

  if (parsed1 && parsed2) {
    return parsed1.format("E.164") === parsed2.format("E.164");
  }

  // Fallback to direct comparison
  return phoneNumber === confirmPhoneNumber;
}
